/**
 * 푸시 구독 해제 및 DB 정리 공통 유틸리티
 *
 * [중요 설계 수칙]
 * 1. 반드시 supabase.auth.signOut() 보다 '먼저' 호출되어야 합니다.
 *    이유: push_subscriptions 테이블의 DELETE RLS 정책은 user_id = auth.uid()를 요구하므로,
 *    signOut 이후에 호출하면 auth.uid()가 소멸되어 DB 행 삭제가 실패합니다.
 */

/**
 * 현재 브라우저의 푸시 구독을 해제하고 push_subscriptions 테이블에서 해당 엔드포인트를 삭제합니다.
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @returns {Promise<void>}
 */
export async function unsubscribePushSubscription(supabase) {
  try {
    // ① 지원 여부 확인: 서비스 워커 및 푸시 매니저 미지원 환경이면 즉시 반환
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return;
    }

    // ② 서비스 워커 등록 조회 (무한 대기 방지 — 3000ms 타임아웃 레이스 필수)
    // 이유: navigator.serviceWorker.ready는 등록된 워커가 없을 때 reject되지 않고
    // 영구히 대기(hang)합니다. try/catch로도 잡히지 않으므로 타임아웃 없이 await하면
    // 로그아웃 버튼이 멈추는 치명적 현상이 발생합니다.
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((resolve) => setTimeout(() => resolve(null), 3000)),
    ]);

    if (!registration) {
      console.warn('[push] ServiceWorker ready 타임아웃(3초) 발생. 구독 해제를 건너뜁니다.');
      return;
    }

    // ③ registration.pushManager.getSubscription() 으로 활성 구독 획득
    const sub = await registration.pushManager.getSubscription();

    // ④ 활성 구독이 없으면 그대로 반환
    if (!sub) {
      return;
    }

    // ⑤ DB 행 삭제를 먼저 실행
    // 이유: 세션이 살아있는 상태에서 본인의 RLS 권한(user_id = auth.uid())으로 삭제를 완결하기 위함
    if (supabase) {
      const { error, count } = await supabase
        .from('push_subscriptions')
        .delete({ count: 'exact' })
        .eq('endpoint', sub.endpoint);

      if (error) {
        console.warn('[push] push_subscriptions DB 행 삭제 실패:', error);
      } else {
        console.log('[push] push_subscriptions DB 행 삭제 성공 (삭제 건수:', count, ')');
      }
    }

    // ⑥ DB 삭제 성공 여부와 무관하게 브라우저 구독 해제 실행
    // 이유: 해제를 건너뛰면 브라우저에 이전 계정의 구독 객체가 살아남아 결함이 지속됩니다.
    // 만약 네트워크 불안정 등으로 DB 삭제에 실패했더라도, 브라우저가 해제되면 다음 발송 시
    // 푸시 서비스(FCM 등)가 404 Not Found 또는 410 Gone을 반환하여 백엔드 push-send가
    // 해당 DB 행을 자동 삭제하므로 자기 치유(Self-healing)됩니다.
    await sub.unsubscribe().catch((unsubErr) => {
      console.warn('[push] 브라우저 푸시 구독 해제(unsubscribe) 예외:', unsubErr);
    });
  } catch (err) {
    // 어떤 경우에도 예외를 밖으로 던지지 않고 경고만 남김 (로그아웃 중단 방지)
    console.warn('[push] 푸시 구독 해제 처리 중 예외 발생:', err);
  }
}

/**
 * 푸시 구독 상태 확인 및 DB 동기화(조용한 복구) 공통 유틸리티
 *
 * [동작 흐름]
 * 1. 브라우저 지원 여부 및 Notification.permission 확인
 * 2. permission !== 'granted' 이면 isSubscribed = false 반환
 * 3. permission === 'granted' 이고 pushManager 구독(sub)이 존재하면:
 *    a) has_push_subscription RPC로 DB 실물 존재 여부 확인
 *    b) DB에 이미 존재하면 isSubscribed = true 반환
 *    c) DB에만 없으면 register_push_subscription RPC로 조용한 복구(Silent Recovery) 수행
 *       - 성공 시 isSubscribed = true 반환
 *       - 실패 시 isSubscribed = false 반환
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @returns {Promise<{ isSupported: boolean, permission: string, isSubscribed: boolean }>}
 */
export async function syncPushSubscription(supabase) {
  // ① 브라우저 API 지원 여부 확인
  const isSupported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

  if (!isSupported) {
    return {
      isSupported: false,
      permission: 'unsupported',
      isSubscribed: false,
    };
  }

  // ② 브라우저 알림 권한 확인
  const currentPerm = Notification.permission;
  if (currentPerm !== 'granted') {
    return {
      isSupported: true,
      permission: currentPerm,
      isSubscribed: false,
    };
  }

  // ③ 서비스 워커 등록 획득 (무한 대기 방지 3초 타임아웃 레이스)
  try {
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((resolve) => setTimeout(() => resolve(null), 3000)),
    ]);

    if (!registration) {
      console.warn('[푸시] ServiceWorker ready 타임아웃(3초) 발생. 동기화를 건너뜁니다.');
      return {
        isSupported: true,
        permission: currentPerm,
        isSubscribed: false,
      };
    }

    // ④ 브라우저 활성 구독(sub) 조회
    const sub = await registration.pushManager.getSubscription();
    if (!sub) {
      return {
        isSupported: true,
        permission: currentPerm,
        isSubscribed: false,
      };
    }

    // ⑤ DB에 실제 구독 행이 존재하는지 RPC로 확인
    if (!supabase) {
      return {
        isSupported: true,
        permission: currentPerm,
        isSubscribed: false,
      };
    }

    const { data: hasSub, error: checkError } = await supabase.rpc('has_push_subscription', {
      p_endpoint: sub.endpoint,
    });

    if (checkError) {
      console.warn('[푸시] has_push_subscription RPC 조회 실패:', checkError);
      return {
        isSupported: true,
        permission: currentPerm,
        isSubscribed: false,
      };
    }

    // ⑥ DB에 이미 정상 등록되어 있는 경우
    if (hasSub) {
      return {
        isSupported: true,
        permission: currentPerm,
        isSubscribed: true,
      };
    }

    // ⑦ 권한 granted + 브라우저 sub 존재 + DB에는 미존재 -> 조용한 복구(Silent Recovery) 수행
    try {
      const jsonSub = sub.toJSON();
      const { error: regError } = await supabase.rpc('register_push_subscription', {
        p_endpoint: sub.endpoint,
        p_p256dh: jsonSub.keys?.p256dh,
        p_auth: jsonSub.keys?.auth,
        p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      });

      if (regError) {
        console.warn('[푸시] 구독 자동 복구 실패:', regError);
        return {
          isSupported: true,
          permission: currentPerm,
          isSubscribed: false,
        };
      }

      console.log('[푸시] 구독 자동 복구 완료 (DB 동기화 성공)');
      return {
        isSupported: true,
        permission: currentPerm,
        isSubscribed: true,
      };
    } catch (syncErr) {
      console.warn('[푸시] 구독 자동 복구 예외 발생:', syncErr);
      return {
        isSupported: true,
        permission: currentPerm,
        isSubscribed: false,
      };
    }
  } catch (err) {
    console.warn('[푸시] 푸시 구독 동기화 처리 중 예외 발생:', err);
    return {
      isSupported: true,
      permission: currentPerm,
      isSubscribed: false,
    };
  }
}

/**
 * base64url 문자열을 Uint8Array 바이트 배열로 변환 (PushManager applicationServerKey 규격)
 * @param {string} base64String
 * @returns {Uint8Array}
 */
export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * 웹 푸시 알림 권한 요청 및 서버 구독 등록 공통 함수
 *
 * [수행 절차]
 * 1. VAPID 공개키 존재 확인 -> 없으면 { ok: false, permission, reason: 'vapid_missing' }
 * 2. Notification.requestPermission() 호출
 * 3. 권한이 'granted'가 아니면 -> { ok: false, permission: permResult, reason: permResult === 'denied' ? 'permission_denied' : 'permission_default' }
 * 4. serviceWorker.ready 워커 획득
 * 5. getSubscription() 조회 -> 없으면 subscribe() 실행
 * 6. register_push_subscription RPC 호출
 * 7. RPC 실패 시, 새로 만든 sub인 경우 unsubscribe() 롤백 후 { ok: false, permission: 'granted', reason: 'subscribe_failed' }
 * 8. 성공 시 -> { ok: true, permission: 'granted', reason: null }
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @returns {Promise<{ ok: boolean, permission: string, reason: 'vapid_missing' | 'permission_denied' | 'permission_default' | 'subscribe_failed' | null }>}
 */
export async function subscribePushNotification(supabase) {
  try {
    const vapidKey = typeof import.meta !== 'undefined' ? import.meta.env.VITE_VAPID_PUBLIC_KEY : null;
    if (!vapidKey) {
      console.warn('[푸시] VAPID 공개키(VITE_VAPID_PUBLIC_KEY)가 설정되지 않았습니다.');
      const currentPerm = typeof Notification !== 'undefined' ? Notification.permission : 'default';
      return { ok: false, permission: currentPerm, reason: 'vapid_missing' };
    }

    // Notification.requestPermission() 호출 (반드시 사용자 클릭 핸들러에서 직접 실행)
    const permResult = await Notification.requestPermission();
    if (permResult !== 'granted') {
      return {
        ok: false,
        permission: permResult,
        reason: permResult === 'denied' ? 'permission_denied' : 'permission_default',
      };
    }

    // serviceWorker.ready 로 등록된 워커 획득 (무한 대기 방지 8초 타임아웃 레이스)
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((resolve) => setTimeout(() => resolve(null), 8000)),
    ]);

    if (!registration) {
      console.warn('[푸시] ServiceWorker ready 타임아웃(8초) 발생. 구독 등록을 중단합니다.');
      return {
        ok: false,
        permission: permResult,
        reason: 'sw_timeout',
      };
    }

    // 기존 구독 확인: getSubscription() 결과가 있으면 재사용
    let sub = await registration.pushManager.getSubscription();
    let isNewSub = false;

    if (!sub) {
      const applicationServerKey = urlBase64ToUint8Array(vapidKey);
      sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
      isNewSub = true;
    }

    // 서버 등록: subscription.toJSON() 추출 후 RPC 호출
    const jsonSub = sub.toJSON();
    const { error: rpcError } = await supabase.rpc('register_push_subscription', {
      p_endpoint: sub.endpoint,
      p_p256dh: jsonSub.keys?.p256dh,
      p_auth: jsonSub.keys?.auth,
      p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    });

    if (rpcError) {
      console.error('[푸시] 푸시 구독 서버 등록 실패:', rpcError);
      // 이번 호출에서 새로 subscribe 한 경우에만 unsubscribe 로 롤백
      if (isNewSub) {
        await sub.unsubscribe().catch(() => {});
      }
      return { ok: false, permission: 'granted', reason: 'subscribe_failed' };
    }

    console.log('[푸시] 푸시 구독 및 서버 등록 완료');
    return { ok: true, permission: 'granted', reason: null };
  } catch (err) {
    console.error('[푸시] 푸시 구독 처리 중 예외 발생:', err);
    return {
      ok: false,
      permission: typeof Notification !== 'undefined' ? Notification.permission : 'default',
      reason: 'subscribe_failed',
    };
  }
}

/**
 * 브라우저 알림 권한 실시간 변화 감지 구독 유틸리티
 *
 * [동작 원리]
 * 1. navigator.permissions.query({ name: 'notifications' }) 로 PermissionStatus 획득
 * 2. 'change' 이벤트 발생 시 callback(status.state) 호출
 * 3. 미지원 브라우저나 query() reject 시 조용히 no-op 후 빈 cleanup 반환
 *
 * @param {(state: 'granted' | 'denied' | 'prompt') => void} callback - 권한 상태 변경 시 호출되는 콜백
 * @returns {() => void} 구독 해제(cleanup) 함수
 */
export function watchNotificationPermission(callback) {
  // ① 브라우저 API 지원 여부 확인 (미지원 환경 안전 방어)
  if (
    typeof window === 'undefined' ||
    typeof navigator === 'undefined' ||
    !navigator.permissions ||
    typeof navigator.permissions.query !== 'function'
  ) {
    return () => {};
  }

  let permissionStatus = null;
  let isCleanedUp = false;

  const handleChange = () => {
    if (isCleanedUp || !permissionStatus) return;
    try {
      callback?.(permissionStatus.state);
    } catch (err) {
      console.warn('[푸시] 권한 변경 콜백 실행 중 예외 발생:', err);
    }
  };

  // ② permissions.query 호출 (일부 브라우저의 'notifications' 미지원/reject 대비 try/catch)
  try {
    const queryPromise = navigator.permissions.query({ name: 'notifications' });
    if (queryPromise && typeof queryPromise.then === 'function') {
      queryPromise
        .then((status) => {
          if (isCleanedUp || !status) return;
          permissionStatus = status;
          if (typeof status.addEventListener === 'function') {
            status.addEventListener('change', handleChange);
          } else {
            status.onchange = handleChange;
          }
        })
        .catch((err) => {
          // query Promise reject 시 조용히 흡수
          console.warn('[푸시] permissions.query("notifications") 미지원/거부:', err);
        });
    }
  } catch (err) {
    // 동기 예외 발생 시 조용히 흡수
    console.warn('[푸시] permissions.query 호출 예외 발생:', err);
    return () => {};
  }

  // ③ cleanup 함수 반환
  return () => {
    isCleanedUp = true;
    if (permissionStatus) {
      try {
        if (typeof permissionStatus.removeEventListener === 'function') {
          permissionStatus.removeEventListener('change', handleChange);
        } else if (permissionStatus.onchange === handleChange) {
          permissionStatus.onchange = null;
        }
      } catch {
        // cleanup 실패 무시
      }
      permissionStatus = null;
    }
  };
}

